import { memo, useRef, useState } from 'react';
import { Typography } from '@mui/material';
import Tooltip from '@mui/material/Tooltip';

export type Props = {
    /** Text to display; when the cell overflows, the full value is shown in the tooltip. */
    value: string;
    /** Max width for single-line ellipsis (default matches campaigns name column). */
    maxWidth?: number | string;
    /** For tests and E2E; defaults so tables can query name cells. */
    dataTestId?: string;
};

/**
 * Single-line text with CSS ellipsis; tooltip shows the full value only when overflowed.
 */
const TruncatedTextWithTooltip = memo(
    ({ value, maxWidth = '200px', dataTestId = 'truncated-text-with-tooltip' }: Props) => {
        const ref = useRef<HTMLElement>(null);
        const [isOverflowing, setIsOverflowing] = useState(false);

        const handleMouseEnter = () => {
            if (ref.current) {
                setIsOverflowing(ref.current.scrollWidth > ref.current.clientWidth);
            }
        };

        return (
            <Tooltip title={isOverflowing ? value : ''}>
                <Typography
                    ref={ref}
                    data-testid={dataTestId}
                    onMouseEnter={handleMouseEnter}
                    sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        maxWidth,
                    }}
                >
                    {value}
                </Typography>
            </Tooltip>
        );
    }
);

TruncatedTextWithTooltip.displayName = 'TruncatedTextWithTooltip';

export default TruncatedTextWithTooltip;
